import type { Aabb2, Point2 } from '../geometry/point';
import type { SelectionMergeMode } from '../model/selection';

/**
 * Which items a marquee collects.
 *
 * Follows which side of its anchor the pointer is on, live: dragging back across
 * the anchor flips the box's meaning, the way it does in every CAD package. It is
 * NOT latched at the first horizontal movement — doing that let a stray pixel of
 * jitter at the very start decide the whole gesture, which is indistinguishable
 * from random to the person dragging.
 */
export type MarqueeRule =
  /** Left to right: only items entirely inside the box. */
  | 'enclosed'
  /** Right to left: anything the box touches, except whatever contains it. */
  | 'crossing';

/**
 * What a marquee release can do to the existing selection.
 *
 * Only two of the four `SelectionMergeMode` values are reachable from a drag:
 * the toggle modifier is the only one a drag has left to speak to merging with
 * — the other modifier inverts the selection granularity for as long as it is
 * held instead. `subtract` needs no gesture of its own: dragging with the
 * toggle modifier held over something already selected takes it out.
 */
export type MarqueeMergeMode = Extract<
  SelectionMergeMode,
  'replace' | 'toggle'
>;

export type MarqueeState = {
  /** Latest pointer position. */
  readonly pointer: Point2;
  /**
   * Corner the box grows from, and the reference the rule is measured against.
   *
   * Starts at the press point, and moves when the translate modifier moves the
   * box — which is exactly why the rule is measured from here rather than from
   * where the press landed: the translation carries the anchor with it, so it
   * cannot reinterpret the gesture's direction, and "the rule stays fixed if
   * the box is moved" falls out of the geometry instead of needing a latch.
   */
  readonly anchor: Point2;
  /** `null` until the drag has moved horizontally at all, then the live direction. */
  readonly rule: MarqueeRule | null;
  /** Fixed by the modifiers at the press; nothing mid-drag changes it. */
  readonly mergeMode: MarqueeMergeMode;
  readonly isTranslateModifierHeld: boolean;
  /** Escape was pressed: the box is gone and the release does nothing. */
  readonly isAborted: boolean;
};

export const beginMarquee = (
  point: Point2,
  { isToggleModifierHeld = false } = {},
): MarqueeState => ({
  pointer: point,
  anchor: point,
  rule: null,
  mergeMode: isToggleModifierHeld ? 'toggle' : 'replace',
  isTranslateModifierHeld: false,
  isAborted: false,
});

/** The box, in the same screen space the points were given in. */
export const marqueeRect = (state: MarqueeState): Aabb2 => {
  const [pointerX, pointerY] = state.pointer;
  const [anchorX, anchorY] = state.anchor;
  return [
    Math.min(anchorX, pointerX),
    Math.min(anchorY, pointerY),
    Math.max(anchorX, pointerX),
    Math.max(anchorY, pointerY),
  ];
};

/**
 * Which side of the anchor the pointer is on, or `null` when it is level with it.
 *
 * Level means the box has no horizontal extent, so there is no direction to read
 * and the previous answer stands.
 */
const directionRule = (pointer: Point2, anchor: Point2): MarqueeRule | null => {
  if (pointer[0] === anchor[0]) {
    return null;
  }
  return pointer[0] > anchor[0] ? 'enclosed' : 'crossing';
};

/** Border treatment. Solid encloses, dashed crosses — the AutoCAD convention. */
export const marqueeBorderStyle = (state: MarqueeState): 'solid' | 'dashed' =>
  state.rule === 'crossing' ? 'dashed' : 'solid';

/** True when the box has any area to collect with. */
export const isMarqueeLive = (state: MarqueeState): boolean => {
  const [minX, minY, maxX, maxY] = marqueeRect(state);
  return !state.isAborted && (maxX > minX || maxY > minY);
};

/**
 * Advances the drag to a new pointer position.
 *
 * With the translate modifier held the box keeps its size and rides along with
 * the pointer; the anchor moves with it, which is what lets later releasing the
 * modifier resume resizing from where the box now sits rather than snapping
 * back to where the drag began.
 */
export const moveMarquee = (
  state: MarqueeState,
  point: Point2,
): MarqueeState => {
  if (state.isAborted) {
    return state;
  }

  if (state.isTranslateModifierHeld) {
    const deltaX = point[0] - state.pointer[0];
    const deltaY = point[1] - state.pointer[1];
    return {
      ...state,
      pointer: point,
      anchor: [state.anchor[0] + deltaX, state.anchor[1] + deltaY],
    };
  }

  return {
    ...state,
    pointer: point,
    rule: directionRule(point, state.anchor) ?? state.rule,
  };
};

export const setMarqueeTranslateModifier = (
  state: MarqueeState,
  isTranslateModifierHeld: boolean,
): MarqueeState =>
  state.isAborted || state.isTranslateModifierHeld === isTranslateModifierHeld
    ? state
    : { ...state, isTranslateModifierHeld };

/** Escape: the box vanishes and the release does nothing at all. */
export const abortMarquee = (state: MarqueeState): MarqueeState =>
  state.isAborted ? state : { ...state, isAborted: true };
